import { forwardRef, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

const base =
  'w-full rounded-md border bg-surface px-2.5 text-ink placeholder:text-ink-subtle transition-colors ' +
  'disabled:cursor-not-allowed disabled:bg-sunken';

const state = (invalid?: boolean) =>
  invalid ? 'border-fail focus:border-fail' : 'border-border hover:border-border-strong';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid === true ? true : undefined}
      className={cn(base, state(invalid), 'h-9', className)}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, rows = 3, ...props }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid === true ? true : undefined}
      className={cn(base, state(invalid), 'py-2 leading-relaxed', className)}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

/**
 * Native select: fully keyboard accessible and consistent with the platform,
 * which is worth more here than a custom listbox.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, invalid, children, ...props }, ref) => (
    <select
      ref={ref}
      aria-invalid={invalid === true ? true : undefined}
      className={cn(base, state(invalid), 'h-9 pr-8', className)}
      {...props}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';
